import { useState, useEffect } from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';
import { ArrowLeft, Save } from 'lucide-react';
import { useSupplierStore } from '../../hooks';
import { Button, Card, CardContent, useToast, Input } from '@lancar-pos/ui';

export function SupplierFormPage() {
  const navigate = useNavigate();
  const { supplierId } = useParams({ from: '/app/inventory/suppliers/$supplierId' });
  const isNew = supplierId === 'new';
  
  const { suppliers, addSupplier, updateSupplier, generateSupplierCode } = useSupplierStore();
  const toast = useToast();
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    contactPerson: '',
    phone: '',
    email: '',
    address: '',
    notes: '',
    isActive: true,
  });

  useEffect(() => {
    if (!isNew && supplierId) {
      const supplier = suppliers.find((s) => s.id === supplierId);
      if (supplier) {
        setFormData({
          name: supplier.name,
          code: supplier.code,
          contactPerson: supplier.contactPerson || '',
          phone: supplier.phone || '',
          email: supplier.email || '',
          address: supplier.address || '',
          notes: supplier.notes || '',
          isActive: supplier.isActive,
        });
      }
    } else {
      generateSupplierCode().then((code) => {
        setFormData((prev) => ({ ...prev, code }));
      });
    }
  }, [isNew, supplierId, suppliers, generateSupplierCode]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Nama supplier wajib diisi');
      return;
    }
    
    if (!formData.code.trim()) {
      toast.error('Kode supplier wajib diisi');
      return;
    }

    setIsSubmitting(true);
    
    try {
      if (isNew) {
        await addSupplier(formData);
        toast.success('Supplier berhasil ditambahkan');
      } else {
        await updateSupplier(supplierId, formData);
        toast.success('Supplier berhasil diperbarui');
      }
      navigate({ to: '/app/inventory/suppliers' });
    } catch (error) {
      toast.error(isNew ? 'Gagal menambahkan supplier' : 'Gagal memperbarui supplier');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-30">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate({ to: '/app/inventory/suppliers' })}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <h1 className="text-xl font-bold text-gray-900">
              {isNew ? 'Tambah Supplier' : 'Edit Supplier'}
            </h1>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto p-4 lg:p-6">
        <form onSubmit={handleSubmit}>
          <Card>
            <CardContent className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Kode Supplier"
                  value={formData.code}
                  onChange={(value: string) => setFormData({ ...formData, code: value })}
                  placeholder="SUP-001"
                  required
                />
                
                <Input
                  label="Nama Supplier"
                  value={formData.name}
                  onChange={(value: string) => setFormData({ ...formData, name: value })}
                  placeholder="Nama supplier"
                  required
                />
                
                <Input
                  label="Person Kontak"
                  value={formData.contactPerson}
                  onChange={(value: string) => setFormData({ ...formData, contactPerson: value })}
                  placeholder="Nama kontak"
                />
                
                <Input
                  label="Telepon"
                  value={formData.phone}
                  onChange={(value: string) => setFormData({ ...formData, phone: value })}
                  placeholder="08123456789"
                />
                
                <Input
                  label="Email"
                  type="email"
                  value={formData.email}
                  onChange={(value: string) => setFormData({ ...formData, email: value })}
                  placeholder="supplier@email.com"
                />
                
                <div className="flex items-center gap-2 pt-6">
                  <input
                    type="checkbox"
                    id="isActive"
                    checked={formData.isActive}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, isActive: e.target.checked })}
                    className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <label htmlFor="isActive" className="text-sm text-gray-700">
                    Supplier aktif
                  </label>
                </div>
              </div>
              
              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700">Alamat</label>
                <textarea
                  value={formData.address}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="Alamat supplier"
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              
              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700">Catatan</label>
                <textarea
                  value={formData.notes}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Catatan tambahan"
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              
              <div className="flex justify-end gap-3 pt-4">
                <Button 
                  type="button" 
                  variant="secondary" 
                  onClick={() => navigate({ to: '/app/inventory/suppliers' })}
                >
                  Batal
                </Button>
                <Button 
                  type="submit" 
                  disabled={isSubmitting}
                >
                  <Save className="w-4 h-4 mr-2" />
                  {isSubmitting ? 'Menyimpan...' : 'Simpan'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </form>
      </main>
    </div>
  );
}
